import React, { useEffect, useState } from "react";
import { Link as RouterLink, useOutletContext } from "react-router-dom";
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { RiCheckLine, RiFileLine, RiStarFill } from "@remixicon/react";
import { countTutorBookings, countTutorNotes, getTutorCycleHours, getTutorPayouts } from "../../../services/dataService";
import { hasPermission, permissions } from "../../../config/permissions";
import {
  cycleSubtitle,
  documentRows,
  feedbackStats,
  payRows,
  performanceRows,
  performanceSegments,
  reviewsLine,
} from "../../../utils/tutorDetail";
import { countText } from "../../../utils/kitPresentation";
import { egp } from "../../../utils/queuePresentation";
import { isPendingVerification } from "../../../utils/verificationPresentation";
import { tokens } from "../../../utils/figmaTokens";
import { SectionCard, TableCard } from "../../../components/kit/SectionCard";
import { Donut, HalfGauge } from "../../../components/kit/Charts";
import DetailRows from "../../../components/kit/DetailRows";
import { StateAction } from "../../../components/kit/States";
import { TableMessageRow } from "../../../components/kit/TableParts";

// Figma's cards (10982:85741…85907) pad 18 px; three of the four are white.
const cardSx = { p: "18px" };
const whiteCardSx = { ...cardSx, bgcolor: tokens.surface.default };

// Figma's first row (10982:85740) draws its two cards at one height, and Feedback rate's rating
// box (10982:85802) fills what is left of its card. The second row's cards sit at its top.
const firstRowSx = { alignSelf: "stretch" };

// The Verify button's teal (10982:85810), in the states' small size.
const reviewSx = {
  p: "8px 10px",
  minWidth: 0,
  gap: "6px",
  borderRadius: "8px",
  fontSize: 12,
  lineHeight: "16px",
  fontWeight: 500,
  bgcolor: tokens.secondary,
  "&:hover": { bgcolor: tokens.secondary, opacity: 0.9 },
  // MUI's own icon margins (8 px right, -4 px left) would add to the gap and the padding.
  "& .MuiButton-startIcon": { m: 0 },
};

// The outlined primary button (View pay history, 10982:85907).
const outlinedPrimarySx = {
  borderColor: tokens.brand.border,
  color: tokens.text.link,
  "&:hover": { borderColor: tokens.brand.border, bgcolor: tokens.brand.subtle },
};

// Performance metrics' three counts: white cells on a 1 px grid line, a number over a dot and
// its share (10982:85741).
const PerformanceStats = ({ rows }) => (
  <Box
    sx={{
      flex: "1 1 240px",
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: "1px",
      bgcolor: tokens.border.default,
      border: `1px solid ${tokens.border.default}`,
      borderRadius: "12px",
      overflow: "hidden",
    }}
  >
    {rows.map((row) => (
      <Box key={row.key} sx={{ height: 80, px: "14px", display: "flex", flexDirection: "column", justifyContent: "center", gap: "4px", bgcolor: tokens.surface.default }}>
        <Typography variant="titleMetric" sx={{ color: tokens.text.primary }}>
          {row.value}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Box aria-hidden sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: tokens.chart[row.tone] }} />
          <Typography variant="labelMicro" sx={{ color: tokens.text.tertiary }}>
            {row.caption}
          </Typography>
        </Box>
      </Box>
    ))}
  </Box>
);

// The Overview (047 D48–D52), Figma's four cards filled with what is stored: lifetime lesson counts
// (Awaiting outcome where Figma has No-shows), the note rate, the certificates, and this cycle's
// pay. Each read lands alone; one that fails leaves "—".
const OverviewTab = () => {
  const { tutor, sessionsTotal, userRole } = useOutletContext();
  const uid = tutor.id;
  const [counts, setCounts] = useState({});
  const [hours, setHours] = useState(null);
  const [payouts, setPayouts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const now = new Date();
    const one = (key, read) =>
      read()
        .then((n) => !cancelled && setCounts((c) => ({ ...c, [key]: n })))
        .catch((e) => {
          console.error(`count ${key} failed:`, e);
          if (!cancelled) setCounts((c) => ({ ...c, [key]: null }));
        });
    one("finished", () => countTutorBookings(uid, { status: "Finished" }));
    one("cancelled", () => countTutorBookings(uid, { status: "Cancelled" }));
    one("awaiting", () => countTutorBookings(uid, { status: "Pending", when: "past", now }));
    one("withNote", () => countTutorNotes(uid));
    getTutorCycleHours(uid, now)
      .then((h) => !cancelled && setHours(h))
      .catch((e) => console.error("Error loading the cycle hours:", e));
    getTutorPayouts(uid)
      .then((rows) => !cancelled && setPayouts(rows))
      .catch((e) => console.error("Error loading the payouts:", e));
    return () => {
      cancelled = true;
    };
  }, [uid]);

  const performance = { ...counts, total: sessionsTotal };
  const feedback = feedbackStats(counts);
  const documents = documentRows(tutor);
  const canReview = isPendingVerification(tutor) && hasPermission(userRole, permissions.REVIEW_VERIFICATION);
  const canPay = hasPermission(userRole, permissions.EDIT_FINANCIAL);

  return (
    <Box
      sx={{
        display: "grid",
        gap: "16px",
        alignItems: "start",
        gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 714fr) minmax(0, 492fr)" },
      }}
    >
      <SectionCard title="Performance metrics" subtitle="Lifetime, all subjects" subtitleVariant="bodySmall" sx={{ ...cardSx, ...firstRowSx }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: "24px" }}>
          <Donut
            segments={performanceSegments(performance)}
            label="Total sessions"
            value={typeof sessionsTotal === "number" ? countText(sessionsTotal) : "—"}
          />
          <PerformanceStats rows={performanceRows(performance)} />
        </Box>
      </SectionCard>

      <SectionCard title="Feedback rate" subtitle="Notes to the parent after completed lessons" subtitleVariant="bodySmall" sx={{ ...whiteCardSx, ...firstRowSx }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "24px" }}>
          <HalfGauge rate={feedback.rate} value={feedback.rateText} label="Feedback rate" />
          <Box sx={{ flex: "1 1 180px", minWidth: 0 }}>
            <DetailRows rows={feedback.rows} />
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1, display: "flex", flexWrap: "wrap", alignItems: "center", alignContent: "center", gap: "14px", p: "13px 14px", bgcolor: tokens.surface.sunken, borderRadius: "12px" }}>
          <Box sx={{ flex: "1 1 200px", minWidth: 0, display: "flex", alignItems: "center", gap: "8px" }}>
            <RiStarFill size={22} color={tokens.star} aria-hidden />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="bodySmallStrong" sx={{ color: tokens.text.primary }}>
                {reviewsLine(tutor)}
              </Typography>
              <Typography variant="labelMicro" component="div" sx={{ color: tokens.text.tertiary }}>
                Lifetime, from student ratings
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
            <Typography variant="titleMetric" sx={{ color: tokens.text.primary }}>
              {egp(tutor.unpaidEarning)}
            </Typography>
            <Typography variant="labelMicro" sx={{ color: tokens.text.tertiary }}>
              owed now
            </Typography>
          </Box>
        </Box>
      </SectionCard>

      <TableCard
        title="Documents"
        subtitle="Certificates the tutor added in the app"
        action={
          canReview && (
            <Button component={RouterLink} to={`/verification?teacher=${uid}`} variant="contained" startIcon={<RiCheckLine size={18} />} sx={reviewSx}>
              Review
            </Button>
          )
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Document</TableCell>
                <TableCell>Years</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.length === 0 ? (
                <TableMessageRow colSpan={3} emptyText="No certificates yet." />
              ) : (
                documents.map((d) => (
                  <TableRow key={d.key}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "8px",
                            bgcolor: tokens.surface.muted,
                            color: tokens.icon.secondary,
                          }}
                        >
                          <RiFileLine size={20} aria-hidden />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="bodySmallStrong" sx={{ color: tokens.text.primary }}>
                            {d.name}
                          </Typography>
                          {d.organization && (
                            <Typography variant="labelMicro" component="div" sx={{ color: tokens.text.tertiary }}>
                              {d.organization}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{d.years}</TableCell>
                    <TableCell>
                      {d.fileUrl ? (
                        <StateAction outlined href={d.fileUrl}>
                          View
                        </StateAction>
                      ) : (
                        <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
                          No file
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>

      <SectionCard title="Payout & compliance" subtitle={cycleSubtitle(hours?.cycles)} subtitleVariant="labelMicro" sx={whiteCardSx}>
        <DetailRows rows={payRows({ tutor, hours, payouts })} py="9px" line={tokens.border.subtle} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {canPay && (
            <Button size="large" fullWidth variant="contained" component={RouterLink} to="/payouts">
              Open Payouts
            </Button>
          )}
          <Button size="large" fullWidth variant="outlined" component={RouterLink} to={`/tutors/${uid}/pay`} sx={outlinedPrimarySx}>
            View pay history
          </Button>
        </Box>
      </SectionCard>
    </Box>
  );
};

export default OverviewTab;
